import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { isAxiosError } from 'axios';
import { Plus } from 'lucide-react';
import { apiClient } from '@/lib/apiClient';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card, CardHeader, CardTitle } from '@/components/ui/Card';

interface BankSummary {
  id: string;
  name: string;
  description: string | null;
  qualifyThreshold: string;
  disqualifyThreshold: string;
  maxQuestions: number | null;
  selectorStrategy: 'ROUND_ROBIN' | 'GREEDY_INFO_GAIN';
  status: 'ACTIVE' | 'PAUSED' | 'ARCHIVED';
  questionCount: number;
}

const STATUS_CLASSES: Record<string, string> = {
  ACTIVE: 'bg-green-900/40 text-green-200',
  PAUSED: 'bg-amber-900/40 text-amber-200',
  ARCHIVED: 'bg-muted text-muted-foreground',
};

/**
 * Story 106.1 — admin list / create page for qualification banks.
 *
 * A bank is a tenant-authored question bank used by the inbound
 * conversational qualification agent. Each bank carries score
 * thresholds, a selector strategy, and a list of questions edited on
 * the per-bank detail page.
 */
export function QualificationBanksPage() {
  const [banks, setBanks] = useState<BankSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiClient
      .get<BankSummary[]>('/qualification/banks')
      .then(({ data }) => {
        if (!cancelled) setBanks(data);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [refresh]);

  if (loading) {
    return <p className="p-6 text-sm text-muted-foreground">Loading…</p>;
  }

  return (
    <div className="space-y-4 p-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Qualification banks</h1>
        <Button size="sm" onClick={() => setCreateOpen(true)}>
          <Plus className="mr-1 h-4 w-4" /> New bank
        </Button>
      </div>

      <p className="text-sm text-muted-foreground">
        A qualification bank drives the inbound conversational
        qualification agent. Each question carries a weight, a profile
        key, and an optional grading rubric. The agent picks the next
        question, weighs the answer, and emits a verdict
        (qualified / disqualified / needs human review).
      </p>

      {banks.length === 0 && (
        <Card>
          <p className="text-sm text-muted-foreground">
            No banks yet. Create one and add questions before pointing
            an inbound channel at it.
          </p>
        </Card>
      )}

      {banks.length > 0 && (
        <Card className="p-0 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-2 text-left">Name</th>
                <th className="px-4 py-2 text-left">Selector</th>
                <th className="px-4 py-2 text-right">Questions</th>
                <th className="px-4 py-2 text-right">Qualify ≥</th>
                <th className="px-4 py-2 text-right">Disqualify ≤</th>
                <th className="px-4 py-2 text-left">Status</th>
              </tr>
            </thead>
            <tbody>
              {banks.map((bank) => (
                <tr key={bank.id} className="border-t">
                  <td className="px-4 py-2">
                    <Link
                      to={`/admin/qualification-banks/${bank.id}`}
                      className="font-medium text-primary hover:underline"
                    >
                      {bank.name}
                    </Link>
                    {bank.description && (
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        {bank.description}
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-2 text-xs text-muted-foreground">
                    {bank.selectorStrategy}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">
                    {bank.questionCount}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">
                    {Number(bank.qualifyThreshold).toFixed(2)}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">
                    {Number(bank.disqualifyThreshold).toFixed(2)}
                  </td>
                  <td className="px-4 py-2">
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                        STATUS_CLASSES[bank.status] ?? 'bg-muted'
                      }`}
                    >
                      {bank.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {createOpen && (
        <CreateBankDialog
          onClose={() => setCreateOpen(false)}
          onCreated={() => {
            setCreateOpen(false);
            setRefresh((x) => x + 1);
          }}
        />
      )}
    </div>
  );
}

function CreateBankDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [qualifyThreshold, setQualifyThreshold] = useState('0.70');
  const [disqualifyThreshold, setDisqualifyThreshold] = useState('0.25');
  const [maxQuestions, setMaxQuestions] = useState('');
  const [selectorStrategy, setSelectorStrategy] =
    useState<'ROUND_ROBIN' | 'GREEDY_INFO_GAIN'>('ROUND_ROBIN');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleCreate(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await apiClient.post('/qualification/banks', {
        name: name.trim(),
        description: description.trim() || null,
        qualifyThreshold: parseFloat(qualifyThreshold),
        disqualifyThreshold: parseFloat(disqualifyThreshold),
        maxQuestions: maxQuestions ? parseInt(maxQuestions, 10) : null,
        selectorStrategy,
      });
      onCreated();
    } catch (err) {
      const detail = isAxiosError(err)
        ? (err.response?.data as { detail?: string } | undefined)?.detail
        : null;
      setError(detail ?? 'Could not create bank.');
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg rounded-lg bg-surface-3 p-6 text-on-surface shadow-lg">
        <CardHeader>
          <CardTitle>New qualification bank</CardTitle>
          <button
            type="button"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
          >
            ✕
          </button>
        </CardHeader>
        <form onSubmit={handleCreate} className="space-y-3">
          <div className="space-y-1">
            <label className="text-xs font-medium">Name</label>
            <Input
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="GEDU enrolment qualifier"
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium">Description</label>
            <textarea
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full rounded-md border bg-background p-2 text-sm"
              placeholder="What inbound funnel does this bank serve?"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-xs font-medium">Qualify threshold</label>
              <Input
                type="number"
                step="0.01"
                min="0"
                max="1"
                value={qualifyThreshold}
                onChange={(e) => setQualifyThreshold(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium">Disqualify threshold</label>
              <Input
                type="number"
                step="0.01"
                min="0"
                max="1"
                value={disqualifyThreshold}
                onChange={(e) => setDisqualifyThreshold(e.target.value)}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-xs font-medium">Max questions</label>
              <Input
                type="number"
                min="1"
                value={maxQuestions}
                onChange={(e) => setMaxQuestions(e.target.value)}
                placeholder="No cap"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium">Selector</label>
              <select
                value={selectorStrategy}
                onChange={(e) =>
                  setSelectorStrategy(
                    e.target.value as 'ROUND_ROBIN' | 'GREEDY_INFO_GAIN',
                  )
                }
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
              >
                <option value="ROUND_ROBIN">Round robin (order_idx)</option>
                <option value="GREEDY_INFO_GAIN">Greedy (info gain)</option>
              </select>
            </div>
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" size="sm" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" size="sm" disabled={submitting}>
              {submitting ? 'Creating…' : 'Create bank'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
